import { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import { otpService } from '../services/otpService';

export default function VerifyOTPPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const { isDesktop } = useResponsive();
  const inputRefs = useRef([]);
  const [otpValue, setOtpValue] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(30);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  // Check if this is a social signup flow
  const socialSignup = location.state?.socialSignup;
  const socialProvider = location.state?.provider;
  const socialEmail = location.state?.email;

  // Determine if phone or email was used
  const isPhone = socialSignup ? false : !!userProfile?.phone;
  const contact = socialSignup ? socialEmail : (isPhone ? userProfile?.phone : userProfile?.email);
  const maskedContact = contact ? maskContact(contact, isPhone) : '';

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpValue];
    newOtp[index] = value.slice(-1);
    setOtpValue(newOtp);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otpValue];
      pastedData.split('').forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtpValue(newOtp);
      // Focus last filled input or last input
      const lastIndex = Math.min(pastedData.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendTimer === 0) {
      setError('');
      try {
        if (socialSignup) {
          // Resend OTP for social signup
          await bonndleAuthApi.sendSocialOtp();
        } else {
          // Resend OTP for phone signup via unified service
          if (userProfile?.phone) {
            await otpService.resendOtp(userProfile.phone, 'recaptcha-container');
          } else {
            throw new Error('No phone number found');
          }
        }
        setResendTimer(30);
        setOtpValue(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } catch (err) {
        console.error('Failed to resend OTP:', err);
        setError('Failed to resend code. Please try again.');
      }
    }
  };

  const isValid = otpValue.every((digit) => digit !== '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    const otp = otpValue.join('');
    setIsVerifying(true);
    setError('');

    try {
      if (socialSignup) {
        // For social signup, pass OTP to create PIN page
        navigate('/create-pin', {
          state: {
            socialSignup: true,
            provider: socialProvider,
            otp,
          }
        });
      } else {
        // For phone signup, verify OTP via unified service (Termii or Firebase)
        const result = await otpService.verifyOtp(otp);

        if (result.verified) {
          navigate('/create-pin', {
            state: {
              otp,
              phoneSignup: true,
              firebaseToken: result.firebaseToken, // Firebase ID token (phoneVerificationToken)
              otpId: result.otpId, // Firebase verificationId or Termii pinId
            }
          });
        } else {
          setError('Invalid verification code. Please try again.');
          setOtpValue(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
        }
      }
    } catch (err) {
      console.error('OTP verification failed:', err);
      setError(err.message || 'Verification failed. Please try again.');
      setOtpValue(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={() => navigate(socialSignup ? '/' : '/signup')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">
            {socialSignup ? 'Verify Email' : 'Create An Account'}
          </h1>
        </header>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 px-6 py-8 flex flex-col">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {isPhone ? (
                <Smartphone className="text-indigo-600" size={28} />
              ) : (
                <Mail className="text-indigo-600" size={28} />
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Enter verification code</h2>
            <p className="text-gray-600">
              We sent a 6-digit code to{' '}
              <span className="font-semibold text-gray-900">{maskedContact}</span>
            </p>
          </div>

          {/* OTP Input */}
          <div className="flex justify-center gap-2 mb-8">
            {otpValue.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors"
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mb-8">
            Didn't receive the code?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendTimer > 0}
              className={`font-semibold ${
                resendTimer > 0 ? 'text-gray-400' : 'text-indigo-600'
              }`}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
            </button>
          </p>

          <div className="flex-1" />

          {/* Invisible reCAPTCHA container for Firebase Phone Auth resend */}
          <div id="recaptcha-container" />

          <button
            type="submit"
            disabled={!isValid || isVerifying}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-colors ${
              isValid && !isVerifying
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isVerifying ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              'Verify'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

// Helper to mask phone/email
function maskContact(contact, isPhone) {
  if (isPhone) {
    // Show last 4 digits: ••••••1234
    return '••••••' + contact.slice(-4);
  } else {
    // Show first 2 chars and domain: jo••••@example.com
    const [local, domain] = contact.split('@');
    if (local.length <= 2) return contact;
    return local.slice(0, 2) + '••••@' + domain;
  }
}
